<script setup lang="ts">
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import type { Icon } from "@/model/icon";
import { injectStrict } from "@/utils/vue";

defineProps<{
  title: string;
  description: string;
  primaryLabel: string;
  primaryIcon: Icon;
  primaryDisabled?: boolean;
  primaryTestId?: string;
  destructive?: boolean;
}>();

defineEmits<{ primary: [] }>();

const dropdown = injectStrict(dropdownKey);
</script>

<template>
  <div
    v-focus-trap
    class="action-confirm-modal"
    role="alertdialog"
    aria-labelledby="action-confirm-title"
    aria-describedby="action-confirm-message"
    @keydown.tab.stop
  >
    <div class="main-text">
      <h2 id="action-confirm-title" class="title">{{ title }}</h2>
      <p id="action-confirm-message" class="description">{{ description }}</p>
    </div>
    <slot name="extra" />
    <div class="actions">
      <BaseButton
        :data-testid="primaryTestId"
        :disabled="primaryDisabled"
        full-width
        :color="destructive ? 'destructive' : 'primary'"
        :icon-before="primaryIcon"
        @click="$emit('primary')"
      >
        {{ primaryLabel }}
      </BaseButton>
      <BaseButton v-autofocus full-width variant="outlined" color="grey" @click="dropdown.close">
        {{ $t("general.cancel") }}
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "./delete-modal-shared" as shared;
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";

.action-confirm-modal {
  @include shared.delete-modal;

  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;

  .main-text {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .description {
      color: colors.$text-subtle;
    }
  }
}
</style>
